import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAllDocumentsRequestList,
  useGetListForSponsorFilters,
  UseGetRfiListForFilters,
  useRequestApproval,
  useRequestReject,
} from "../../custom-hooks/queries";
import SkeletonLoader from "../../components/SkeletonLoader";
import Pagination from "../../components/CustomPagination";
import Notification from "../../components/Notification";
import PageToolbarFilters from "../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../components/Filters/ToolbarSearch";
import { generateV2ServiceCategoryOptions } from "../../utils/serviceCategoryV2Mapping";
import TableExportMenu from "../../components/Menus/TableExportMenu";
import { documentsCellStyles } from "../../utils/exportBadgeStyles";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import {
  buildProviderVqaExportRecords,
  getLibraryRequestsExportHeaders,
  getLibraryRequestsTableRows,
  LIBRARY_REQUESTS_USE_SERVER_SORT,
  sortConfigToLibraryRequestsApiParams,
} from "../../utils/libraryRequestsGridMapper";
import LibraryRequestsGridTable from "../../components/LibraryRequests/LibraryRequestsGridTable";

const PROVIDER_LIBRARY_REQUESTS_PAGE_KEY = "providerLibraryRequests";

export const ProviderVQAGrid = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    requested_on: { direction: "desc" },
  });
  const [sortFetchNonce, setSortFetchNonce] = useState(0);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    status: "error",
  });

  const { data: sponsors } = useGetListForSponsorFilters();
  const { data: rfis } = UseGetRfiListForFilters();

  const v2ServiceOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  const dispatch = useDispatch();

  const searchQuery =
    useSelector(
      (state) =>
        state.providersFilters.searchTextByPage[PROVIDER_LIBRARY_REQUESTS_PAGE_KEY]
    ) || "";

  const [selectedIds, setSelectedIds] = useState({
    docType: [],
    sponsor: [],
    services: [],
    status: [],
  });

  const filters = useMemo(
    () => [
      {
        id: "sponsor",
        name: "Sponsor",
        searchable: true,
        options: sponsors || [],
        total: sponsors?.length || 0,
      },
      {
        id: "docType",
        name: "Document Type",
        searchable: false,
        options: [
          { docId: "RFI", label: "VQQ", checked: false },
          { docId: "VQA", label: "Audit Report", checked: false },
        ],
        total: 2,
      },
      {
        id: "services",
        name: "Scope",
        searchable: true,
        isV2: true,
        options: v2ServiceOptions,
        total: v2ServiceOptions?.length || 0,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          { statusID: "Released", label: "Released", checked: false },
          { statusID: "Pending", label: "Pending", checked: false },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
        ],
        total: 3,
      },
    ],
    [sponsors, v2ServiceOptions]
  );

  const serviceNames = useMemo(() => {
    return (
      selectedIds.services?.map((serviceId) => {
        const service = rfis?.find(
          (rfi) =>
            rfi.rfi_id === serviceId ||
            rfi.id === serviceId ||
            rfi.hash_id === serviceId
        );
        return (
          service?.services ||
          service?.rfi_name ||
          service?.name ||
          serviceId
        );
      }) || []
    );
  }, [selectedIds.services, rfis]);

  const emptyProviderList = [];
  const rfiIds = [];

  const serverSortParams = useMemo(() => {
    if (!LIBRARY_REQUESTS_USE_SERVER_SORT) return undefined;
    const { orderBy, sortDirection } =
      sortConfigToLibraryRequestsApiParams(sortConfig);
    if (!orderBy || !sortDirection) return undefined;
    return { orderBy, sortDirection };
  }, [sortConfig]);

  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllDocumentsRequestList(
    currentPage,
    selectedIds.docType,
    emptyProviderList,
    selectedIds.status,
    rfiIds,
    [],
    searchQuery,
    selectedIds.sponsor,
    serviceNames,
    serverSortParams,
    sortFetchNonce
  );

  const {
    mutate: approve,
    error: approveError,
    isSuccess: approveSuccess,
    isError: isApproveError,
  } = useRequestApproval();
  const {
    mutate: reject,
    error: rejectError,
    isSuccess: rejectSuccess,
    isError: isRejectError,
  } = useRequestReject();

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    if (LIBRARY_REQUESTS_USE_SERVER_SORT) {
      setSortFetchNonce((n) => n + 1);
      setCurrentPage(1);
    }
  };

  const sortedItems = useMemo(
    () =>
      getLibraryRequestsTableRows(
        requestList,
        sortConfig,
        LIBRARY_REQUESTS_USE_SERVER_SORT,
        "provider"
      ),
    [requestList, sortConfig]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIds, searchQuery]);

  const exportDataRef = useRef(null);

  // Vendors never see risk scoring, so the vendor export carries no Risk
  // Summary and fetches no summaries.
  useEffect(() => {
    exportDataRef.current = () => {
      const records = buildProviderVqaExportRecords(sortedItems);
      return {
        records,
        headers: getLibraryRequestsExportHeaders("provider"),
      };
    };
  }, [sortedItems]);

  useEffect(() => {
    if (isApproveError && approveError) {
      setNotification({
        show: true,
        message: approveError?.message || "Failed to approve request",
        status: "error",
      });
    }
  }, [isApproveError, approveError]);

  useEffect(() => {
    if (isRejectError && rejectError) {
      setNotification({
        show: true,
        message: rejectError?.message || "Failed to reject request",
        status: "error",
      });
    }
  }, [isRejectError, rejectError]);

  useEffect(() => {
    if (approveSuccess) {
      setNotification({
        show: true,
        message: "Request approved successfully",
        status: "success",
      });
    }
  }, [approveSuccess]);

  useEffect(() => {
    if (rejectSuccess) {
      setNotification({
        show: true,
        message: "Request rejected successfully",
        status: "success",
      });
    }
  }, [rejectSuccess]);

  useEffect(() => {
    if (isError && error) {
      setNotification({
        show: true,
        message: error?.message || "An error occurred while loading data",
        status: "error",
      });
    }
  }, [isError, error]);

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">
              Library Requests
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Review and manage document library requests from sponsors: VQQ
              questionnaires and audit reports.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="Library Requests"
              reportSubtitle="Document requests from sponsors"
              filePrefix="Library_Requests_Vendor"
              downloadType="Library Requests"
              cellStyles={documentsCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({
                    pageKey: PROVIDER_LIBRARY_REQUESTS_PAGE_KEY,
                    searchText: text,
                  })
                )
              }
              placeholder="Search documents..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              serviceCategoryV2SourceData={rfis || []}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
          <LibraryRequestsGridTable
            audience="provider"
            data={sortedItems}
            sortConfig={sortConfig}
            onSort={handleSort}
            refetch={refetch}
            isSearchActive={!!searchQuery}
            approve={approve}
            reject={reject}
            approveError={approveError}
            rejectError={rejectError}
            rejectIsError={isRejectError}
          />
        )}

        {!isError &&
          !isLoading &&
          sortedItems &&
          sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                totalPages={
                  requestList?.document_request_for_provider?.total_pages || 1
                }
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
      </div>

      <Notification
        show={notification.show}
        setShow={(show) => setNotification((prev) => ({ ...prev, show }))}
        errorMessage={notification.message}
        status={notification.status}
      />
    </div>
  );
};
